import React from 'react';
import { useMonitoring } from './hooks/useMonitoring';
import { useHealthSummary } from './hooks/useHealthSummary';
import { useCostMetrics } from './hooks/useCostMetrics';
import { useInfrastructureCostMetrics } from './hooks/useInfrastructureCostMetrics';
import { HealthSummaryCard } from './components/HealthSummaryCard';
import { UsageMetricsCard } from './components/UsageMetricsCard';
import { PerformanceMetricsCard } from './components/PerformanceMetricsCard';
import { AgentStepMetrics } from './components/AgentStepMetrics';
import { CostMetricsCard } from './components/CostMetricsCard';
import { InfrastructureCostCard } from './components/InfrastructureCostCard';
import { Button, Alert } from '@/components/ui';

// Chart Icon for Monitoring
const ChartBarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-6 h-6'}
  >
    <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 01-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 013 19.875v-6.75z" />
  </svg>
);

export const MonitoringSection: React.FC = () => {
  const { usageMetrics, performanceMetrics, loading, error, refreshMetrics } =
    useMonitoring();

  const {
    healthSummary,
    loading: healthLoading,
    error: healthError,
    refreshHealthSummary,
  } = useHealthSummary();

  const {
    costSummary,
    costTrends,
    topUsers,
    loading: costLoading,
    error: costError,
    period: costPeriod,
    setPeriod: setCostPeriod,
    refresh: refreshCosts,
  } = useCostMetrics();

  const {
    infraSummary,
    infraTrends,
    combinedSummary,
    loading: infraLoading,
    error: infraError,
    period: infraPeriod,
    setPeriod: setInfraPeriod,
    refresh: refreshInfra,
  } = useInfrastructureCostMetrics();

  const handleRefreshAll = () => {
    refreshMetrics();
    refreshHealthSummary();
    refreshCosts();
    refreshInfra();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <ChartBarIcon className="w-6 h-6 mr-3 text-primary-600" />
          <h2 className="text-xl font-semibold text-secondary-900">
            Service Monitoring
          </h2>
        </div>
        <Button
          variant="outline"
          onClick={handleRefreshAll}
          loading={loading || healthLoading || costLoading || infraLoading}
        >
          Refresh All
        </Button>
      </div>

      {(error || healthError || costError || infraError) && (
        <Alert variant="error">
          {error || healthError || costError || infraError}
        </Alert>
      )}

      {/* Health Summary - Heads-Up Display (Task 060a) */}
      <HealthSummaryCard
        healthSummary={healthSummary}
        loading={healthLoading}
        onRefresh={refreshHealthSummary}
      />

      {/* Platform Cost Monitoring (Task 080) */}
      <CostMetricsCard
        costSummary={costSummary}
        costTrends={costTrends}
        topUsers={topUsers}
        loading={costLoading}
        period={costPeriod}
        onPeriodChange={setCostPeriod}
        onRefresh={refreshCosts}
      />

      {/* Infrastructure Cost Monitoring (Task 081) */}
      <InfrastructureCostCard
        infraSummary={infraSummary}
        infraTrends={infraTrends}
        combinedSummary={combinedSummary}
        loading={infraLoading}
        period={infraPeriod}
        onPeriodChange={setInfraPeriod}
        onRefresh={refreshInfra}
      />

      <UsageMetricsCard metrics={usageMetrics} loading={loading} />
      <PerformanceMetricsCard metrics={performanceMetrics} loading={loading} />
      <AgentStepMetrics metrics={performanceMetrics} loading={loading} />
    </div>
  );
};

export default MonitoringSection;
